import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import Image from "next/legacy/image";
import Navigation from "components/common/Navigation";
import Seo from "components/common/Seo";
import Footer from "components/common/Footer";
import styles from "../../styles/common/SubpageShell.module.css";

interface SubpageShellProps {
  /** Page <title> */
  title: string;
  /** Meta description */
  description: string;
  /** Page path without locale prefix, e.g. "/case-studies" */
  path: string;
  /** Hero heading */
  heading: string;
  /** Hero subtitle */
  subtitle: string;
  /** Optional eyebrow badge above heading */
  eyebrow?: string;
  /** Optional full-bleed background image for the hero */
  heroImage?: string;
  /** Page main content */
  children: ReactNode;
  /** Pass doAnimation to Navigation (default: false) */
  doAnimation?: boolean;
  /** Keep the footer metadata but omit its large CTA section. */
  hideFooterCta?: boolean;
  /** Omit the shared footer when a page provides its own complete ending. */
  hideFooter?: boolean;
  /**
   * Drop the standard hero. For pages that open with a section of their own —
   * the whitepaper download page puts heading and form next to the preview
   * fan — the shell would otherwise repeat that heading above it.
   */
  hideHero?: boolean;
}

const SubpageShell: FunctionComponent<SubpageShellProps> = ({
  title,
  description,
  path,
  heading,
  subtitle,
  eyebrow,
  heroImage,
  children,
  doAnimation = false,
  hideFooterCta = false,
  hideFooter = false,
  hideHero = false
}) => {
  const [isDarkColor, setIsDarkColor] = useState<boolean>(false);
  const { ref, inView } = useInView({ rootMargin: "0% 0% -90%" });

  useEffect(() => {
    setIsDarkColor(inView);
  }, [inView]);

  return (
    <>
      <Seo title={title} description={description} path={path} />

      <Navigation isDarkColor={isDarkColor} doAnimation={doAnimation} />

      {!hideHero && (
        <header
          id="main-content"
          className={`${styles.heroSection} ${
            heroImage ? styles.heroSectionWithImage : ""
          }`}
        >
          {heroImage && (
            <div className={styles.heroImage} aria-hidden="true">
              <Image
                src={heroImage}
                layout="fill"
                objectFit="cover"
                sizes="100vw"
                alt=""
                priority
              />
            </div>
          )}

          <div className={styles.ambientGlow} aria-hidden="true" />
          <div className={styles.ambientGrid} aria-hidden="true" />

          <div className={styles.heroContent}>
            {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
            <h1 className={styles.primaryHeading}>{heading}</h1>
            <h2 className={styles.subHeading}>{subtitle}</h2>
          </div>
        </header>
      )}

      {/* Skip link target moves along when the page brings its own opener. */}
      <main id={hideHero ? "main-content" : undefined}>{children}</main>

      {!hideFooter && (
        <div className={styles.footerArea} ref={ref}>
          <Footer hideFinalCta={hideFooterCta} />
        </div>
      )}
    </>
  );
};

export default SubpageShell;
